import React, { useState, useEffect, useRef } from 'react';
import {
  Grid, Typography, Box, Button, alpha,
  useTheme, useMediaQuery, TextField, InputAdornment,
  Stack, Chip, Paper, CircularProgress,
} from '@mui/material';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import SearchIcon from '@mui/icons-material/Search';
import GroupsIcon from '@mui/icons-material/Groups';
import AgentCard from '../components/agent/AgentCard';
import EditAgentModal from '../components/agent/EditAgentModal';
import AddAgentModal from '../components/agent/AddAgentModal';
import axios from 'axios';
import { BASE_URL } from 'config/constant';
import { getToken } from 'Service/Service';
import toast from 'react-hot-toast';
import { useAppDispatch, useAppSelector } from '../app/hooks';
import { fetchAgentsAsync, selectAgents } from '../app/services/agents/agentSlice';
import { IAgent } from 'definitions/app/agent';
import { addAgent } from '../app/services/agents/agentAPI';

const SkeletonCard = () => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 2,
        p: 2,
        height: 120,
        bgcolor: alpha(theme.palette.grey[500], 0.04),
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
      }}
    >
      <Box sx={{ width: 44, height: 44, borderRadius: '50%', bgcolor: alpha(theme.palette.grey[500], 0.15) }} />
      <Box sx={{ flex: 1 }}>
        <Box sx={{ height: 14, width: '55%', bgcolor: alpha(theme.palette.grey[500], 0.15), borderRadius: 1, mb: 1 }} />
        <Box sx={{ height: 11, width: '40%', bgcolor: alpha(theme.palette.grey[500], 0.1), borderRadius: 1 }} />
      </Box>
    </Box>
  );
};

const AgentManagement: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const dispatch = useAppDispatch();
  const { agents, status } = useAppSelector(selectAgents);

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedAgent, setSelectedAgent] = useState<IAgent | null>(null);
  const [isEditModalOpen, setEditModalOpen] = useState(false);
  const [isAddAgentModalOpen, setAddAgentModalOpen] = useState(false);
  const effectRan = useRef(false);

  useEffect(() => {
    if (effectRan.current === false) {
      dispatch(fetchAgentsAsync());
      return () => { effectRan.current = true; };
    }
  }, [dispatch]);

  const handleToggleActive = async (mobile: string) => {
    try {
      const { data } = await axios.post(
        `${BASE_URL}agent/block`,
        { mobile },
        { headers: { encryption: false, token: getToken() } }
      );
      if (data) {
        toast.success(data.message);
        dispatch(fetchAgentsAsync());
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to toggle status');
    }
  };

  const handleUpdate = async (updatedAgent: IAgent) => {
    try {
      const payload = {
        name: updatedAgent.name,
        mobile: updatedAgent.mobile,
        address: updatedAgent.address,
        active: updatedAgent.active,
        fare: updatedAgent.fare,
        kg14_refill: updatedAgent.kg14_refill,
        kg14_nc: updatedAgent.kg14_nc,
        kg14_dbc: updatedAgent.kg14_dbc,
        kg19_refill: updatedAgent.kg19_refill,
        kg19_nc: updatedAgent.kg19_nc,
        kg5_ftl_refill: updatedAgent.kg5_ftl_refill,
        kg5_ftl_nc: updatedAgent.kg5_ftl_nc,
        kg5_domestic: updatedAgent.kg5_domestic,
      };
      const { data } = await axios.post(
        `${BASE_URL}agent/update`,
        payload,
        { headers: { encryption: false, token: getToken() } }
      );
      if (data?.status === 'success') {
        toast.success('Updated successfully');
        setEditModalOpen(false);
        dispatch(fetchAgentsAsync());
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Update failed');
    }
  };

  const handleEdit = (agent: IAgent) => {
    setSelectedAgent(agent);
    setEditModalOpen(true);
  };

  const handleAddAgent = async (agentData: IAgent) => {
    try {
      const result = await addAgent(agentData);
      if (result.status === 'success') {
        toast.success('Agent added successfully');
        setAddAgentModalOpen(false);
        dispatch(fetchAgentsAsync());
      } else {
        toast.error(result.message || 'Failed to add agent');
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to add agent');
    }
  };

  const filteredAgents = agents.filter((agent: IAgent) =>
    (agent?.name || '').toString().toLowerCase().includes((searchTerm || '').toLowerCase())
  );

  const isLoading = status === 'loading';
  const activeCount = agents.filter((a) => a.active).length;

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
      {/* Page Header */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 60%, ${theme.palette.primary.light} 100%)`,
          px: { xs: 2, sm: 4 },
          py: { xs: 2.5, sm: 3.5 },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          borderRadius: 1,
          boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.4)}`,
        }}
      >
        <Stack direction="row" alignItems="center" gap={2}>
          <Box sx={{ bgcolor: alpha('#fff', 0.15), borderRadius: 2, p: 1, display: 'flex', alignItems: 'center' }}>
            <SupportAgentIcon sx={{ color: '#fff', fontSize: { xs: 28, sm: 36 } }} />
          </Box>
          <Box>
            <Typography variant={isMobile ? 'h5' : 'h4'} fontWeight={700} sx={{ color: '#fff', lineHeight: 1.2 }}>
              Agent Management
            </Typography>
            <Typography variant="body2" sx={{ color: alpha('#fff', 0.8), mt: 0.3 }}>
              Manage delivery agents and their pricing
            </Typography>
          </Box>
        </Stack>

        <Button
          variant="contained"
          startIcon={<PersonAddIcon />}
          onClick={() => setAddAgentModalOpen(true)}
          sx={{
            bgcolor: alpha('#fff', 0.2),
            color: '#fff',
            borderRadius: 2,
            fontWeight: 600,
            textTransform: 'none',
            border: `1px solid ${alpha('#fff', 0.3)}`,
            flexShrink: 0,
            '&:hover': { bgcolor: alpha('#fff', 0.3) },
            display: { xs: 'none', sm: 'flex' },
          }}
        >
          Add Agent
        </Button>
      </Box>

      <Box sx={{ px: { xs: 1, sm: 3 }, pt: 2, pb: 4 }}>
        <Box
          sx={{
            bgcolor: 'background.paper',
            borderRadius: 3,
            boxShadow: `0 2px 12px ${alpha(theme.palette.grey[500], 0.12)}`,
            overflow: 'hidden',
          }}
        >
          {/* Toolbar */}
          <Box
            sx={{
              px: { xs: 1.5, sm: 3 },
              py: 2,
              borderBottom: `1px solid ${theme.palette.divider}`,
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              flexWrap: 'wrap',
            }}
          >
            <TextField
              size="small"
              placeholder="Search agents by name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
                  </InputAdornment>
                ),
              }}
              sx={{ flex: 1, minWidth: 200, maxWidth: 400 }}
            />

            <Stack direction="row" spacing={1} ml="auto" flexWrap="wrap" useFlexGap>
              <Chip
                icon={<GroupsIcon sx={{ fontSize: '14px !important' }} />}
                label={`${agents.length} Total`}
                size="small"
                sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1), color: 'primary.dark', fontWeight: 600 }}
              />
              <Chip
                label={`${activeCount} Active`}
                size="small"
                sx={{ bgcolor: alpha(theme.palette.success.main, 0.1), color: 'success.dark', fontWeight: 600 }}
              />
              <Button
                variant="contained"
                size="small"
                startIcon={<PersonAddIcon />}
                onClick={() => setAddAgentModalOpen(true)}
                sx={{
                  borderRadius: 2,
                  fontWeight: 600,
                  textTransform: 'none',
                  display: { xs: 'flex', sm: 'none' },
                  boxShadow: `0 3px 10px ${alpha(theme.palette.primary.main, 0.35)}`,
                }}
              >
                Add
              </Button>
            </Stack>
          </Box>

          {/* Agent grid */}
          <Box sx={{ px: { xs: 1.5, sm: 3 }, py: 2.5 }}>
            {isLoading ? (
              <Grid container spacing={2}>
                {[...Array(6)].map((_, i) => (
                  <Grid item xs={12} sm={6} md={4} key={i}>
                    <SkeletonCard />
                  </Grid>
                ))}
              </Grid>
            ) : filteredAgents.length === 0 ? (
              <Paper
                elevation={0}
                sx={{
                  p: 5,
                  textAlign: 'center',
                  border: `1px dashed ${theme.palette.divider}`,
                  borderRadius: 2,
                }}
              >
                <SupportAgentIcon sx={{ fontSize: 40, color: alpha(theme.palette.text.secondary, 0.3), mb: 1 }} />
                <Typography color="text.secondary">
                  {searchTerm ? 'No agents match your search.' : 'No agents found.'}
                </Typography>
              </Paper>
            ) : (
              <Grid container spacing={2}>
                {filteredAgents.map((agent: IAgent) => (
                  <Grid item xs={12} sm={6} md={4} key={agent.mobile}>
                    <AgentCard
                      agent={agent}
                      onEdit={handleEdit}
                      onToggleActive={handleToggleActive}
                    />
                  </Grid>
                ))}
              </Grid>
            )}
          </Box>
        </Box>
      </Box>

      <EditAgentModal
        open={isEditModalOpen}
        onClose={() => setEditModalOpen(false)}
        agent={selectedAgent}
        onUpdate={handleUpdate}
      />
      <AddAgentModal
        open={isAddAgentModalOpen}
        onClose={() => setAddAgentModalOpen(false)}
        onAddAgent={handleAddAgent}
      />
    </Box>
  );
};

export default AgentManagement;
